import { motion } from "framer-motion";
import { fadeUp, stagger } from "./motion";
import { IMAGES } from "@/lib/site";

const chapters = [
  {
    n: "01",
    title: "The Red-Tape Problem",
    body: "Government funds are slow, limited, and buried in bureaucracy. By the time appropriations move, families are already on the street. The system is broken by design.",
    accent: "#0A2472",
  },
  {
    n: "02",
    title: "The Solana Solution",
    body: "Solana settles in seconds for fractions of a cent. HRS routes community capital directly to the front lines — no committees, no red tape, no delay. Empathy, on-chain.",
    accent: "#00f0ff",
  },
  {
    n: "03",
    title: "The Impact",
    body: "Every buy fuels crisis accommodation, street outreach, and case management. We build shelters, fund job training, and restore dignity — meme energy converted into real roofs.",
    accent: "#ffb800",
  },
];

export const Mission = () => {
  return (
    <section id="mission" className="relative py-24 sm:py-32 px-5 sm:px-6" data-testid="mission-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff007f] mb-4">The Manifesto</div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-5xl lg:text-6xl">
            From <span className="text-stroke">cold streets</span> to warm homes.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Mission image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative rounded-2xl overflow-hidden border border-white/10 min-h-[340px]"
          >
            <img src={IMAGES.mission} alt="From crisis to hope" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <div className="font-mono text-xs text-white/60">Cold crisis → warm hope</div>
              <div className="font-display font-bold text-lg text-white">This is the mission.</div>
            </div>
          </motion.div>

          {/* Chapters */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="md:col-span-7 grid gap-6"
          >
            {chapters.map((c) => (
              <motion.div
                key={c.n}
                variants={fadeUp}
                className="group relative rounded-2xl border border-white/10 bg-[#101010] p-7 sm:p-8 hover:border-[color:var(--hc)] transition-colors"
                style={{ "--hc": c.accent }}
                data-testid={`mission-chapter-${c.n}`}
              >
                <div className="flex items-start gap-5">
                  <span
                    className="font-display font-black text-4xl sm:text-5xl leading-none shrink-0"
                    style={{ color: c.accent === "#0A2472" ? "#3b5bdb" : c.accent }}
                  >
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-display font-bold uppercase tracking-tight text-lg sm:text-xl mb-2">{c.title}</h3>
                    <p className="font-mono text-sm text-white/60 leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
